import { notification } from "antd";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { getAuthErrorCode } from "./auth/authTypes";
import { getToken, logout } from "./services/auth";
import { ensureSession, SessionLimitError } from "./services/session";
import { API_VERSION, shouldAttachBackendSession } from "./services/sessionRoutes";

declare module "axios" {
  interface AxiosRequestConfig {
    skipErrorNotification?: boolean;
    retriedAfterTokenRefresh?: boolean;
  }
}

const baseURL = process.env.NODE_ENV === "development" ? "" : process.env.REACT_APP_BASE_URL || "";

export const axiosInstance = axios.create({
  baseURL,
  headers: {
    "X-API-Version": API_VERSION,
  },
  withCredentials: true,
  timeout: 30_000,
});

let forcedLogoutPromise: Promise<void> | null = null;

const shouldShowErrorNotification = (error: { config?: { skipErrorNotification?: boolean } }) =>
  !error?.config?.skipErrorNotification;

const redirectToLogin = () => {
  if (typeof window !== "undefined") {
    window.location.replace("/auth/login");
  }
};

const triggerForcedLogout = () => {
  if (!forcedLogoutPromise) {
    forcedLogoutPromise = (async () => {
      try {
        await logout({ silent: true });
      } catch (logoutError) {
        console.error("Forced logout failed", logoutError);
      } finally {
        redirectToLogin();
      }
    })();
  }

  return forcedLogoutPromise;
};

// Request Interceptor - attach token
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.headers) {
      if (!config.headers["X-API-Version"]) {
        config.headers["X-API-Version"] = API_VERSION;
      }

      const method = config.method?.toLowerCase();
      if (method && ["post", "put", "patch", "delete"].includes(method)) {
        if (!config.headers["Idempotency-Key"]) {
          config.headers["Idempotency-Key"] = uuidv4();
        }
      }

      if (shouldAttachBackendSession(config.url)) {
        try {
          const sessionId = await ensureSession();
          if (sessionId) {
            config.headers["X-Session-ID"] = sessionId;
          }
        } catch (error) {
          if (error instanceof SessionLimitError) {
            if (typeof window !== "undefined") {
              window.location.href = "/auth/sessions";
            }
          }
          throw error;
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response Interceptor - handle session warnings, 401, and other errors
axiosInstance.interceptors.response.use(
  (response) => {
    // Surface backend hijack / session warnings
    const sessionWarning = response.headers?.["x-session-warning"];
    if (sessionWarning && typeof sessionWarning === "string") {
      notification.warning({
        message: "Session Security Warning",
        description: sessionWarning,
        duration: 8,
      });
    }
    return response;
  },
  async (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        const requestConfig = error.config as
          | (typeof error.config & { retriedAfterTokenRefresh?: boolean })
          | undefined;

        if (requestConfig && !requestConfig.retriedAfterTokenRefresh) {
          const refreshedToken = await getToken(true);
          if (refreshedToken) {
            requestConfig.retriedAfterTokenRefresh = true;
            requestConfig.headers = requestConfig.headers ?? {};
            requestConfig.headers.Authorization = `Bearer ${refreshedToken}`;
            return axiosInstance.request(requestConfig);
          }
        }

        const errorCode = getAuthErrorCode(error.response.data) || "";
        const isForcedLogout =
          errorCode === "session_revoked_by_user" || errorCode === "AUTH_SESSION_REVOKED_BY_USER";

        if (!forcedLogoutPromise) {
          notification.error({
            message: isForcedLogout ? "Session revoked" : "Session expired",
            description: isForcedLogout
              ? "Your session was revoked from another device. Please log in again."
              : "Please log in again to continue.",
          });
        }
        await triggerForcedLogout();
        return Promise.reject(error);
      }

      if (shouldShowErrorNotification(error)) {
        notification.error({
          message: `Error: ${error.response.status}`,
          description: error.response.data?.message || "Something went wrong. Please try again.",
        });
      }
    } else if (error.request) {
      if (shouldShowErrorNotification(error)) {
        notification.error({
          message: "Network Error",
          description: "No response from server. Please check your connection.",
        });
      }
    } else {
      if (shouldShowErrorNotification(error)) {
        notification.error({
          message: "Unexpected Error",
          description: error.message,
        });
      }
    }

    return Promise.reject(error);
  },
);
